<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'
import { useConviteStore } from './store/useConviteStore'
import TermosModal from '@/pages/admin/auth/cadastrar/components/TermosModal.vue'
import * as rules from '@/validators/cdf-rules'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import logo from '@images/logo-right.png'
import logoDark from '@images/logo-right-white.png'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const themeLogo = useGenerateImageVariant(logo, logoDark)
const route = useRoute('convite-token')
const store = useConviteStore()

const {
  form,
  data,
  convite,
  erro,
  loading,
  salvando,
} = storeToRefs(store)

const {
  validarConvite,
  aceitarConvite,
} = store

const isPasswordVisible = ref(false)
const showTermosModal = ref(false)

const isTermosAccepted = computed(() => data.value.termos === true)

const tipoLabel = computed(() =>
  convite.value?.role === 'espaco-eventos' ? 'Espaço para Eventos' : 'Fornecedor')

const prazoLabel = computed(() => {
  const meses = convite.value?.duracao_meses ?? 0

  if (meses === 12)
    return '1 ano'

  if (meses % 12 === 0)
    return `${meses / 12} anos`

  return `${meses} ${meses === 1 ? 'mês' : 'meses'}`
})

const handleTermosAccepted = () => {
  data.value.termos = true
}

onMounted(() => {
  validarConvite(String(route.params.token))
})
</script>

<template>
  <div class="justify-center auth-wrapper d-flex align-center pa-4">
    <div
      class="position-relative my-sm-16 w-100"
      style="max-width: 600px;"
    >
      <!-- 👉 Top shape -->
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <!-- 👉 Bottom shape -->
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <VCard
        class="auth-card"
        width="100%"
        :class="$vuetify.display.smAndUp ? 'pa-6' : 'pa-3'"
      >
        <VCardItem class="justify-center pa-0">
          <VCardTitle>
            <img
              :src="themeLogo"
              height="80"
              alt="Connect Eventos"
            >
          </VCardTitle>
        </VCardItem>

        <!-- 👉 Validando o token -->
        <VCardText
          v-if="loading"
          class="py-10 text-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="48"
          />
          <p class="mt-4 mb-0 text-body-2">
            Validando seu convite...
          </p>
        </VCardText>

        <!-- 👉 Convite indisponível -->
        <VCardText
          v-else-if="!convite"
          class="py-8 text-center"
        >
          <VIcon
            icon="tabler-link-off"
            size="56"
            color="error"
            class="mb-4"
          />
          <h5 class="mb-2 text-h6">
            Convite indisponível
          </h5>
          <p class="mb-6 text-body-2">
            {{ erro }}
          </p>
          <VBtn
            variant="tonal"
            :to="{ name: 'admin-auth-cadastrar' }"
          >
            Fazer cadastro normal
          </VBtn>
        </VCardText>

        <!-- 👉 Formulário de aceite -->
        <template v-else>
          <VCardText class="pb-2">
            <h5 class="mb-1 text-center text-h6 text-primary">
              Seu convite exclusivo
            </h5>
            <p class="mb-0 text-center text-body-2">
              Cadastre-se como <strong>{{ tipoLabel }}</strong> e divulgue seus
              produtos e serviços sem nenhum custo.
            </p>
          </VCardText>

          <VCardText>
            <!-- Benefício concedido -->
            <VCard
              variant="tonal"
              color="success"
              class="mb-5"
            >
              <VCardText class="pa-4">
                <div class="mb-3 d-flex align-center gap-3">
                  <VIcon
                    icon="tabler-gift"
                    size="28"
                    color="success"
                    class="flex-shrink-0"
                  />
                  <div>
                    <p class="mb-0 text-subtitle-2 font-weight-bold">
                      {{ convite.plano.titulo }} gratuito por {{ prazoLabel }}
                    </p>
                    <p class="mb-0 text-caption">
                      Sem cobrança e sem cartão de crédito.
                    </p>
                  </div>
                </div>

                <VList
                  v-if="convite.plano.beneficios?.length"
                  density="compact"
                  bg-color="transparent"
                  class="pa-0"
                >
                  <VListItem
                    v-for="(beneficio, index) in convite.plano.beneficios"
                    :key="index"
                    class="min-height-0 px-0"
                  >
                    <template #prepend>
                      <VIcon
                        icon="tabler-check"
                        size="18"
                        color="success"
                        class="me-2"
                      />
                    </template>
                    <VListItemTitle class="text-caption">
                      {{ beneficio }}
                    </VListItemTitle>
                  </VListItem>
                </VList>

                <p
                  v-if="convite.expira_em"
                  class="mt-3 mb-0 text-caption"
                >
                  <VIcon
                    icon="tabler-clock"
                    size="16"
                    class="me-1"
                  />
                  Convite válido até {{ new Date(`${convite.expira_em}T00:00:00`).toLocaleDateString('pt-BR') }}
                </p>
              </VCardText>
            </VCard>

            <VForm ref="form">
              <VRow>
                <VCol
                  cols="12"
                  class="py-1"
                >
                  <AppTextField
                    v-model="data.nome_fantasia"
                    :rules="[rules.requiredValidator]"
                    label="Nome Fantasia"
                    placeholder="Digite o Nome Fantasia"
                  />
                </VCol>

                <VCol
                  cols="12"
                  class="py-1"
                >
                  <AppTextField
                    v-model="data.cnpj"
                    v-mask="data.cnpj.length > 14 ? '##.###.###/####-##' : '###.###.###-##'"
                    :rules="[rules.requiredValidator]"
                    label="CPF/CNPJ"
                    placeholder="Ex: 000.000.000-00 ou 00.000.000/0000-00"
                  />
                </VCol>

                <VDivider class="mx-3 my-4" />

                <VCol
                  cols="12"
                  class="py-1"
                >
                  <AppTextField
                    v-model="data.nome_completo"
                    :rules="[rules.requiredValidator]"
                    label="Nome Completo"
                    placeholder="Digite seu nome completo"
                  />
                </VCol>

                <VCol
                  cols="12"
                  class="py-1"
                >
                  <CDFTextField
                    v-model="data.email"
                    :rules="[rules.requiredValidator, rules.emailValidator]"
                    label="Email"
                    type="email"
                    placeholder="johndoe@email.com"
                  />
                </VCol>

                <VCol
                  cols="12"
                  class="py-1"
                >
                  <CDFTextField
                    v-model="data.password"
                    :rules="[rules.requiredValidator]"
                    label="Senha"
                    placeholder="············"
                    :type="isPasswordVisible ? 'text' : 'password'"
                    :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                    @click:append-inner="isPasswordVisible = !isPasswordVisible"
                  />

                  <VAlert
                    v-if="!isTermosAccepted"
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="my-4"
                  >
                    <template #prepend>
                      <VIcon icon="tabler-info-circle" />
                    </template>
                    <div class="gap-2 d-flex flex-column">
                      <span class="text-sm">
                        Para continuar, você precisa ler e aceitar nossos Termos de Uso e Política de Privacidade.
                      </span>
                      <VBtn
                        color="primary"
                        variant="tonal"
                        size="small"
                        @click="showTermosModal = true"
                      >
                        <VIcon
                          icon="tabler-file-text"
                          start
                        />
                        Ler Termos e Política
                      </VBtn>
                    </div>
                  </VAlert>

                  <VAlert
                    v-else
                    type="success"
                    variant="tonal"
                    density="compact"
                    class="my-4"
                  >
                    <template #prepend>
                      <VIcon icon="tabler-check" />
                    </template>
                    <div class="d-flex justify-space-between align-center">
                      <span class="text-sm font-weight-medium">
                        Você leu e aceitou os Termos de Uso e Política de Privacidade
                      </span>
                      <VBtn
                        color="success"
                        variant="text"
                        size="small"
                        @click="showTermosModal = true"
                      >
                        Reler
                      </VBtn>
                    </div>
                  </VAlert>

                  <VBtn
                    :loading="salvando"
                    :disabled="salvando || !isTermosAccepted"
                    block
                    @click="aceitarConvite"
                  >
                    Ativar meu acesso gratuito
                  </VBtn>
                </VCol>

                <VCol
                  cols="12"
                  class="text-base text-center"
                >
                  <span class="d-inline-block">Já tem uma conta?</span>
                  <RouterLink
                    class="text-primary ms-1 d-inline-block"
                    :to="{ name: 'admin-auth-login' }"
                  >
                    Em vez disso, faça login
                  </RouterLink>
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </template>
      </VCard>
    </div>

    <TermosModal
      v-model="showTermosModal"
      :user-type="convite?.role ?? 'fornecedor'"
      @accepted="handleTermosAccepted"
    />
  </div>
</template>

<style lang="scss" scoped>
.min-height-0 {
  min-block-size: 0;
}
</style>
